/**
 * My Listings Page - User's marketplace listings
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Package, Filter, Loader2 } from 'lucide-react';
import { api } from '../../../services/api';
import { ListingCard } from '../../../components/marketplace';
import { useLanguage } from '../../../contexts/LanguageContext';

type StatusFilter = 'all' | 'active' | 'sold' | 'removed';

export default function MyListings() {
  const { t } = useLanguage();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-listings', statusFilter],
    queryFn: () =>
      api.getMyMarketplaceListings({
        status: statusFilter === 'all' ? undefined : statusFilter,
        limit: 50,
      }),
  });

  const markSoldMutation = useMutation({
    mutationFn: (listingId: string) => api.markMarketplaceListingSold(listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (listingId: string) => api.deleteMarketplaceListing(listingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-listings'] });
    },
  });

  const listings = data?.data || [];

  const handleMarkSold = async (listingId: string) => {
    if (confirm(t('myListings.confirm.markSold', 'Mark this listing as sold?'))) {
      markSoldMutation.mutate(listingId);
    }
  };

  const handleDelete = async (listingId: string) => {
    if (confirm(t('myListings.confirm.delete', 'Are you sure you want to delete this listing?'))) {
      deleteMutation.mutate(listingId);
    }
  };

  const statusCounts = {
    all: listings.length,
    active: listings.filter((l) => l.status === 'active').length,
    sold: listings.filter((l) => l.status === 'sold').length,
    removed: listings.filter((l) => l.status === 'removed').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('myListings.title', 'My Listings')}</h1>
          <p className="text-gray-600 mt-1">
            {t('myListings.subtitle', 'Manage your marketplace listings')}
          </p>
        </div>
        <Link
          to="/account/marketplace/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          {t('myListings.createListing', 'Create Listing')}
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2">
        <Filter className="w-4 h-4 text-gray-400 flex-shrink-0" />
        {(['all', 'active', 'sold', 'removed'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === status
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            <span className="ml-1 text-xs opacity-70">
              ({statusCounts[status]})
            </span>
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg">
          {t('myListings.error.loadFailed', 'Failed to load listings. Please try again.')}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && listings.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {statusFilter === 'all' ? t('myListings.empty.noListings', 'No listings yet') : t('myListings.empty.noStatusListings', `No ${statusFilter} listings`, { status: statusFilter })}
          </h3>
          <p className="text-gray-600 mb-6">
            {statusFilter === 'all'
              ? t('myListings.empty.startSelling', 'Start selling your IKEA furniture to others nearby.')
              : t('myListings.empty.noStatusListingsDesc', `You don't have any ${statusFilter} listings at the moment.`, { status: statusFilter })}
          </p>
          {statusFilter === 'all' && (
            <Link
              to="/account/marketplace/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('myListings.createFirstListing', 'Create Your First Listing')}
            </Link>
          )}
        </div>
      )}

      {/* Listings Grid */}
      {!isLoading && !error && listings.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              showActions
              onEdit={() => window.location.href = `/account/marketplace/edit/${listing.id}`}
              onMarkSold={() => handleMarkSold(listing.id)}
              onDelete={() => handleDelete(listing.id)}
            />
          ))}
        </div>
      )}

      {/* Stats Summary */}
      {!isLoading && listings.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{statusCounts.active}</p>
            <p className="text-sm text-gray-600">{t('myListings.stats.active', 'Active')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">{statusCounts.sold}</p>
            <p className="text-sm text-gray-600">{t('myListings.stats.sold', 'Sold')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-400">{statusCounts.removed}</p>
            <p className="text-sm text-gray-600">{t('myListings.stats.removed', 'Removed')}</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary-600">{listings.length}</p>
            <p className="text-sm text-gray-600">{t('myListings.stats.total', 'Total')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
